// React Compiler opt-out: translation fetch effect keyed on legacy model identities;
// compiling changes its re-fire cadence. Convert deps to primitive ids before compiling.
'use no memo';

import {Divider, Icon} from '@albert-io/atomic';
import {getCardClassName} from '@albert-io/atomic/utils/getCardClassName';
import type {
  GuessModelV1,
  QuestionModelV3,
  QuestionSetModelV1,
  SubjectModelV2
} from '@albert-io/models';
import classNames from 'classnames';
import SplitPane from 'client/components/SplitPane/SplitPane.react';
import {debounce} from 'lodash';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useShallow} from 'zustand/react/shallow';

import CopyrightModal from 'client/components/CopyrightModal/CopyrightModal.react';
import {ANNOTATION_GUTTER_CLASS} from 'client/components/PracticeView/Annotations/annotationGutter';

import DraftGuessWrapper from '../../../../AssignmentPracticeView/DraftGuesses/DraftGuessWrapper';

import {
  Explanation,
  GuessCorrectnessBanner,
  InteractivityHandler,
  Prompt,
  Standards,
  StandardsTable,
  Tags
} from '../ContentBlocks';
import {questionStemRegex} from '../questionStem';

import type QuestionTypeStore from '../../QuestionType.store';
import {getAssignmentIdFromPath} from '../../assignmentIdFromPath';
import {HeadingBar} from '../HeadingBar/HeadingBar.react';
import HowOthersAnswered from '../HowOthersAnswered';
import PostSubmissionScrollPoint from '../PostSubmissionScrollPoint/PostSubmissionScrollPoint';
import '../QuestionWrapper/question-wrapper.scss';
import {useQuestionTranslationStore, useShowTranslationToggle} from '../TranslationSection';
import './question-component.scss';

import {ArchivedQuestionBanner} from 'client/components/ArchivedQuestions/ArchivedQuestionBanner.react';
import CONSTANTS from 'client/constants';

import ReviewAnswersInEnglishBanner from 'client/ReviewAnswersInEnglishBanner';

interface PreGuessQuestionContentProps {
  activeQuestion: QuestionModelV3;
  activeQuestionStore?: QuestionTypeStore;
  ActiveQuestionInputArea: React.ComponentType<any>;
  interactive?: boolean;
}

const PreGuessQuestionContent = ({
  activeQuestion,
  activeQuestionStore,
  ActiveQuestionInputArea,
  interactive
}: PreGuessQuestionContentProps) => {
  return (
    <DraftGuessWrapper
      storeName={activeQuestion.getStoreName()}
      submitted={false}
      store={activeQuestionStore}
    >
      <InteractivityHandler interactive={interactive}>
        <ActiveQuestionInputArea
          isGuessSubmitted={false}
          isGuessCorrect={false}
          storeName={activeQuestion.getStoreName()}
          question={activeQuestion}
          questionId={activeQuestion.getId()}
        />
      </InteractivityHandler>
    </DraftGuessWrapper>
  );
};

interface PostGuessQuestionContentProps {
  activeQuestion: QuestionModelV3;
  activeQuestionSet: QuestionSetModelV1;
  activeQuestionStore?: QuestionTypeStore;
  activeSubject: SubjectModelV2;
  ActiveQuestionInputArea: React.ComponentType<any>;
  customExplanation?: React.ReactElement<any>; // custom explanation component
  customHOA?: React.ReactElement<any>; // custom how others answered component
  customResultIndicator?: React.ReactElement<any>; // custom result indicator component
  isRedesign?: boolean;
  guess?: GuessModelV1;
  postSubmissionScrollPointRef?: React.RefObject<any>;
  partialCreditEnabled?: boolean;
}

const PostGuessQuestionContent = ({
  activeQuestion,
  activeQuestionSet,
  activeSubject,
  ActiveQuestionInputArea,
  customExplanation,
  customHOA,
  customResultIndicator,
  guess,
  isRedesign,
  postSubmissionScrollPointRef,
  partialCreditEnabled = false
}: PostGuessQuestionContentProps) => {
  return (
    <DraftGuessWrapper storeName={activeQuestion.getStoreName()} submitted>
      <ActiveQuestionInputArea
        isGuessSubmitted
        isGuessCorrect={guess?.isCorrect()}
        guess={guess}
        storeName={activeQuestion.getStoreName()}
        question={activeQuestion}
        questionId={activeQuestion.getId()}
      />
      <PostSubmissionScrollPoint ref={postSubmissionScrollPointRef} />
      {customResultIndicator ?? (
        <GuessCorrectnessBanner guess={guess} partialCreditEnabled={partialCreditEnabled} />
      )}
      {customExplanation ?? <Explanation question={activeQuestion} />}
      {customHOA ?? <HowOthersAnswered question={activeQuestion} />}
      {!isRedesign && activeSubject.isShouldShowLabelTable() && (
        <StandardsTable question={activeQuestion} />
      )}
      {!isRedesign && (
        <Standards
          question={activeQuestion}
          subject={activeSubject}
          questionSet={activeQuestionSet}
        />
      )}
      {!isRedesign && (
        <Tags question={activeQuestion} subject={activeSubject} questionSet={activeQuestionSet} />
      )}
    </DraftGuessWrapper>
  );
};

interface DennisQuestionContentProps {
  activeQuestion: QuestionModelV3;
  activeQuestionStore?: QuestionTypeStore;
  ActiveQuestionInputArea: React.ComponentType<any>;
  interactive?: boolean;
}

const DennisQuestionContent = ({
  activeQuestion,
  activeQuestionStore,
  ActiveQuestionInputArea,
  interactive
}: DennisQuestionContentProps) => {
  return (
    <InteractivityHandler interactive={interactive}>
      <ActiveQuestionInputArea
        storeName={activeQuestionStore?.getName?.()}
        question={activeQuestion}
        questionId={activeQuestion.getId()}
        activeQuestionStore={activeQuestionStore}
      />
    </InteractivityHandler>
  );
};

interface QuestionContentProps {
  questionState: 'PreGuess' | 'PostGuess' | 'Dennis';
}

type CombinedProps = QuestionContentProps &
  (PreGuessQuestionContentProps | PostGuessQuestionContentProps | DennisQuestionContentProps);
const QuestionContent = (props: CombinedProps) => {
  const {questionState, ...rest} = props;

  if (questionState === 'PreGuess') {
    const {activeQuestion, activeQuestionStore, ActiveQuestionInputArea, interactive} =
      rest as PreGuessQuestionContentProps;
    return (
      <PreGuessQuestionContent
        activeQuestion={activeQuestion}
        activeQuestionStore={activeQuestionStore}
        ActiveQuestionInputArea={ActiveQuestionInputArea}
        interactive={interactive}
      />
    );
  }

  if (questionState === 'Dennis') {
    const {activeQuestion, activeQuestionStore, ActiveQuestionInputArea, interactive} =
      rest as DennisQuestionContentProps;
    return (
      <DennisQuestionContent
        activeQuestion={activeQuestion}
        activeQuestionStore={activeQuestionStore}
        ActiveQuestionInputArea={ActiveQuestionInputArea}
        interactive={interactive}
      />
    );
  }

  const {
    activeQuestion,
    activeQuestionSet,
    activeQuestionStore,
    activeSubject,
    ActiveQuestionInputArea,
    customExplanation,
    customHOA,
    customResultIndicator,
    guess,
    isRedesign,
    postSubmissionScrollPointRef,
    partialCreditEnabled = false
  } = rest as PostGuessQuestionContentProps;
  return (
    <PostGuessQuestionContent
      activeQuestion={activeQuestion}
      activeQuestionSet={activeQuestionSet}
      activeQuestionStore={activeQuestionStore}
      activeSubject={activeSubject}
      ActiveQuestionInputArea={ActiveQuestionInputArea}
      customExplanation={customExplanation}
      customHOA={customHOA}
      customResultIndicator={customResultIndicator}
      guess={guess}
      isRedesign={isRedesign}
      postSubmissionScrollPointRef={postSubmissionScrollPointRef}
      partialCreditEnabled={partialCreditEnabled}
    />
  );
};

export interface QuestionComponentProps
  extends
    QuestionContentProps,
    PreGuessQuestionContentProps,
    PostGuessQuestionContentProps,
    DennisQuestionContentProps {
  actions: React.ReactElement<any>;
  answerInputPaneRef?: React.RefObject<HTMLDivElement | null>;
  customHeader?: React.ReactNode;
  displayOrientation: 'default' | 'vertical' | 'horizontal';
  hasHeading?: boolean;
  hasQuestionSetAccessPredicate: (questionSet: QuestionSetModelV1) => boolean;
  headingActions: React.ReactNode;
  /** Rendered as the first child of the question heading row, before the title. */
  leadingHeaderAction?: React.ReactNode;
  /** Keep the question title on one line, ellipsised, instead of wrapping. */
  truncateTitle?: boolean;
  isMobile?: boolean;
  modifyTitle?: (args: any) => void;
  navigateBackProps?: {
    disabled: boolean;
    handleNavigation: () => void;
  };
  navigateNextProps?: {
    disabled: boolean;
    handleNavigation: () => void;
  };
  resizerPosition?: number;
  onResize?: (size: number) => void;
  showDifficulty: boolean;
  stackedContainerRef?: React.RefObject<HTMLDivElement | null>;
  partialCreditEnabled?: boolean;
  shouldSetContentMaxWidth?: boolean;
  useAdaptiveResizer?: boolean;
  shouldShowCopyright?: boolean;
}

type QuestionComponentContentSplitProps = Omit<
  QuestionComponentProps,
  'displayOrientation' | 'headingActions' | 'showDifficulty'
>;

const PANE_MIN_WIDTH = 280;
const CONTAINER_PADDING = 32;

const QuestionComponentContentSplit = ({
  actions,
  activeQuestion,
  activeQuestionSet,
  activeQuestionStore,
  activeSubject,
  ActiveQuestionInputArea,
  answerInputPaneRef,
  customExplanation,
  customHOA,
  customResultIndicator,
  guess,
  hasQuestionSetAccessPredicate,
  interactive,
  navigateBackProps,
  navigateNextProps,
  onResize,
  postSubmissionScrollPointRef,
  questionState,
  resizerPosition,
  partialCreditEnabled = false,
  shouldSetContentMaxWidth,
  useAdaptiveResizer,
  shouldShowCopyright
}: QuestionComponentContentSplitProps) => {
  const splitPaneRef = useRef<HTMLDivElement | null>(null);
  const leftCardRef = useRef<HTMLDivElement | null>(null);
  const rightCardRef = useRef<HTMLDivElement | null>(null);
  // using the width of the split pane to determine the max size of the left pane (in other words, enforcing the min size for the right pane)
  // falling back on using the viewport width minus padding since we can assume that the split pane will be close to the full width of the viewport
  const [elementSize, setElementSize] = useState<number>(window.innerWidth - CONTAINER_PADDING);

  // overrideSize is used to programmatically reset the split position (e.g. on double-click)
  const [overrideSize, setOverrideSize] = useState<string | undefined>(undefined);
  const overrideResetTimeoutRef = useRef<number | null>(null);

  const handleResizerDoubleClick = useCallback(() => {
    // Clear any pending reset timeout before scheduling a new one
    if (overrideResetTimeoutRef.current !== null) {
      clearTimeout(overrideResetTimeoutRef.current);
      overrideResetTimeoutRef.current = null;
    }

    setOverrideSize('50%');
    // Clear the override after one render so SplitPane resumes uncontrolled mode
    // (SplitPane retains the '50%' position via its internal draggedSize state)
    overrideResetTimeoutRef.current = window.setTimeout(() => {
      setOverrideSize(undefined);
      overrideResetTimeoutRef.current = null;
    }, 0);
  }, []);

  useEffect(() => {
    return () => {
      if (overrideResetTimeoutRef.current !== null) {
        clearTimeout(overrideResetTimeoutRef.current);
        overrideResetTimeoutRef.current = null;
      }
    };
  }, []);

  const {showTranslations} = useQuestionTranslationStore(
    useShallow((state) => ({
      showTranslations: state.showTranslations
    }))
  );

  useEffect(() => {
    const handleResize = () => {
      const newSize =
        splitPaneRef.current?.getBoundingClientRect().width ??
        window.innerWidth - CONTAINER_PADDING;
      setElementSize(newSize);
    };

    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  });

  // Sync both cards to the same height so content starts at the same line
  // while the pane's grid + marginBlock:'auto' keeps them vertically centered.
  useEffect(() => {
    if (!useAdaptiveResizer) return;

    const left = leftCardRef.current;
    const right = rightCardRef.current;
    if (!left || !right) return;

    let lastHeight = 0;

    const measure = () => {
      // Clear fixed heights so scrollHeight reflects natural content size
      left.style.height = 'auto';
      right.style.height = 'auto';

      const maxH = Math.max(left.scrollHeight, right.scrollHeight);

      if (maxH !== lastHeight) {
        lastHeight = maxH;
      }

      left.style.height = `${maxH}px`;
      right.style.height = `${maxH}px`;
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(left);
    observer.observe(right);
    return () => observer.disconnect();
  });

  const showTranslationToggle = useShowTranslationToggle();

  const leftCard = (
    <div
      className={classNames(
        'question-wrapper question-split-pane instructions-pane h-full',
        {
          'question-split-pane--adaptive': useAdaptiveResizer
        },
        getCardClassName({roundness: showTranslationToggle ? 'none' : 'small'})
      )}
    >
      <div
        className={classNames(
          'question-wrapper__body relative px-x4 xs:px-x2',
          ANNOTATION_GUTTER_CLASS,
          {
            'mx-auto! w-full max-w-[600px]!': shouldSetContentMaxWidth,
            'flex justify-center': useAdaptiveResizer,
            'p-x4 xs:p-x2': !useAdaptiveResizer
          }
        )}
      >
        {/* w-full so percentage-width embeds don't collapse to their intrinsic
            size when the adaptive resizer makes this a flex item */}
        <div className='w-full'>
          <Prompt question={activeQuestion} variant='promptWithoutStem' />
        </div>
      </div>
    </div>
  );

  const rightCard = (
    <form
      className={classNames(
        'question-wrapper question-split-pane answer-options-pane h-full',
        {
          'question-split-pane--adaptive': useAdaptiveResizer
        },
        getCardClassName({roundness: showTranslationToggle ? 'none' : 'small'})
      )}
      onSubmit={(e) => {
        e.preventDefault();
      }}
    >
      <div
        ref={answerInputPaneRef}
        className='question-wrapper__body relative flex h-full flex-col'
      >
        <div
          className={classNames('flex grow items-start px-x4 xs:px-x2', ANNOTATION_GUTTER_CLASS, {
            'mx-auto! w-full max-w-[600px]!': shouldSetContentMaxWidth,
            'p-x4 xs:p-x2': !useAdaptiveResizer
          })}
        >
          <div className='w-full'>
            {activeQuestion?.getPrompt()?.match(questionStemRegex) && (
              <div>
                <Prompt question={activeQuestion} variant='stem' />
                <Divider spacing={4} />
              </div>
            )}
            {showTranslations && <ReviewAnswersInEnglishBanner className='py-x1' />}
            <QuestionContent
              activeQuestion={activeQuestion}
              activeQuestionSet={activeQuestionSet}
              activeQuestionStore={activeQuestionStore}
              activeSubject={activeSubject}
              ActiveQuestionInputArea={ActiveQuestionInputArea}
              customExplanation={customExplanation}
              customHOA={customHOA}
              customResultIndicator={customResultIndicator}
              guess={guess}
              interactive={interactive}
              isRedesign
              postSubmissionScrollPointRef={postSubmissionScrollPointRef}
              questionState={questionState}
              partialCreditEnabled={partialCreditEnabled}
            />
          </div>
        </div>
        {shouldShowCopyright && <CopyrightModal />}
        {actions &&
          React.cloneElement(actions, {
            guess,
            hasAccess: hasQuestionSetAccessPredicate?.(activeQuestionSet) || false,
            navigateBackProps,
            navigateNextProps,
            question: activeQuestion,
            questionSet: activeQuestionSet,
            subject: activeSubject
          })}
      </div>
    </form>
  );
  const debouncedOnResize = useMemo(
    () => debounce((size: any) => onResize?.(size), 100),
    [onResize]
  );

  useEffect(() => {
    return () => {
      debouncedOnResize.cancel();
    };
  }, [debouncedOnResize]);

  return (
    <div
      ref={splitPaneRef}
      className={classNames('split-content-wrapper h-full', {
        'flex flex-col': showTranslationToggle
      })}
    >
      <SplitPane
        allowResize
        primary='first'
        split='vertical'
        maxSize={elementSize - PANE_MIN_WIDTH}
        minSize={PANE_MIN_WIDTH}
        defaultSize={resizerPosition ?? '50%'}
        size={overrideSize}
        onChange={debouncedOnResize}
        onResizerDoubleClick={handleResizerDoubleClick}
        style={{position: 'relative'}}
        resizerClassName={classNames({
          'resizer-grabber question-component-resizer': !useAdaptiveResizer,
          'adaptive-question-resizer': useAdaptiveResizer
        })}
        resizerChildren={
          useAdaptiveResizer ? null : <Icon icon='grip-dots-vertical' color='brand' />
        }
        pane1Style={{
          display: 'grid',
          gridTemplateRows: '1fr',
          alignItems: 'center',
          justifyContent: 'stretch',
          overflow: 'auto'
        }}
        pane2Style={{
          display: 'grid',
          gridTemplateRows: '1fr',
          alignItems: 'center',
          justifyContent: 'stretch',
          overflow: 'auto'
        }}
      >
        {useAdaptiveResizer ? <div ref={leftCardRef}>{leftCard}</div> : leftCard}
        {useAdaptiveResizer ? <div ref={rightCardRef}>{rightCard}</div> : rightCard}
      </SplitPane>
    </div>
  );
};

const QuestionComponentHorizontalLayout = ({
  actions,
  activeQuestion,
  activeQuestionSet,
  activeQuestionStore,
  activeSubject,
  ActiveQuestionInputArea,
  customExplanation,
  customHOA,
  customResultIndicator,
  guess,
  hasQuestionSetAccessPredicate,
  interactive,
  navigateBackProps,
  navigateNextProps,
  postSubmissionScrollPointRef,
  questionState,
  stackedContainerRef,
  partialCreditEnabled,
  shouldSetContentMaxWidth,
  shouldShowCopyright
}: QuestionComponentContentSplitProps) => {
  const {showTranslations} = useQuestionTranslationStore(
    useShallow((state) => ({
      showTranslations: state.showTranslations && state.translationSettings.question
    }))
  );
  return (
    <div ref={stackedContainerRef} className='question-component__horizontal-layout-wrapper'>
      <div
        className={classNames('question-component__horizontal-layout__question-wrapper', {
          'grid! place-items-center': shouldSetContentMaxWidth
        })}
      >
        <div
          className={classNames({
            'w-full!': shouldSetContentMaxWidth,
            'border border-border-primary': !shouldSetContentMaxWidth
          })}
        >
          <div
            className={classNames(
              'question-wrapper horizontal-layout__instructions-card',
              {
                'w-full!': shouldSetContentMaxWidth
              },
              'rounded-none background-primary'
            )}
          >
            <div
              className={classNames(
                'question-wrapper__body flex items-center p-x4 xs:p-x2',
                ANNOTATION_GUTTER_CLASS,
                {
                  'w-full max-w-[600px]!': shouldSetContentMaxWidth
                }
              )}
            >
              {/* change padding when translation toggle is available */}
              {/* w-full so percentage-width embeds (iframes) don't collapse to their
                  intrinsic size inside the flex item */}
              <div className='w-full'>
                <Prompt question={activeQuestion} variant='promptWithoutStem' />
              </div>
            </div>
          </div>
          <form
            className={classNames(
              'question-wrapper horizontal-layout__answer-options-card',
              {
                'w-full!': shouldSetContentMaxWidth
              },
              'rounded-none background-primary'
            )}
            onSubmit={(e) => {
              e.preventDefault();
            }}
          >
            <div
              className={classNames('question-wrapper__body', {
                'max-w-[600px]': shouldSetContentMaxWidth
              })}
            >
              {(activeQuestionStore || questionState === 'Dennis') && (
                <div className={classNames('p-x4 xs:p-x2', ANNOTATION_GUTTER_CLASS)}>
                  {activeQuestion?.getPrompt()?.match(questionStemRegex) && (
                    <div>
                      <Prompt question={activeQuestion} variant='stem' />
                      <Divider spacing={4} />
                    </div>
                  )}
                  {showTranslations && <ReviewAnswersInEnglishBanner className='py-x1' />}
                  <QuestionContent
                    activeQuestion={activeQuestion}
                    activeQuestionSet={activeQuestionSet}
                    activeQuestionStore={activeQuestionStore}
                    activeSubject={activeSubject}
                    ActiveQuestionInputArea={ActiveQuestionInputArea}
                    customExplanation={customExplanation}
                    customHOA={customHOA}
                    customResultIndicator={customResultIndicator}
                    guess={guess}
                    interactive={interactive}
                    isRedesign
                    postSubmissionScrollPointRef={postSubmissionScrollPointRef}
                    questionState={questionState}
                    partialCreditEnabled={partialCreditEnabled}
                  />
                </div>
              )}
              {shouldShowCopyright && <CopyrightModal />}
              {actions &&
                React.cloneElement(actions, {
                  guess,
                  hasAccess: hasQuestionSetAccessPredicate?.(activeQuestionSet) || false,
                  navigateBackProps,
                  navigateNextProps,
                  question: activeQuestion,
                  questionSet: activeQuestionSet,
                  subject: activeSubject
                })}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export const QuestionComponent = ({
  actions,
  activeQuestion,
  activeQuestionSet,
  activeQuestionStore,
  activeSubject,
  answerInputPaneRef,
  ActiveQuestionInputArea,
  customExplanation,
  customHeader,
  customHOA,
  customResultIndicator,
  displayOrientation,
  guess,
  hasHeading = true,
  hasQuestionSetAccessPredicate,
  headingActions,
  leadingHeaderAction,
  truncateTitle,
  interactive,
  isMobile,
  modifyTitle,
  navigateBackProps,
  navigateNextProps,
  onResize,
  postSubmissionScrollPointRef,
  questionState,
  resizerPosition,
  showDifficulty,
  stackedContainerRef,
  partialCreditEnabled,
  shouldSetContentMaxWidth = false,
  useAdaptiveResizer = false,
  shouldShowCopyright = true
}: QuestionComponentProps) => {
  const {init, reset, isReady, fetchTranslation, showTranslations} = useQuestionTranslationStore(
    useShallow((state) => ({
      init: state.init,
      reset: state.reset,
      fetchTranslation: state.fetchTranslation,
      isReady: state.isReady,
      showTranslations: state.showTranslations
    }))
  );

  const isArchivedQuestion = activeQuestion['status'] === CONSTANTS.QUESTIONSET_STATUSES.ARCHIVED;

  // The header is hidden in assignment / assignment-builder contexts — derive it from the route
  // during render instead of forcing it false via effects.
  const shouldShowHeader =
    hasHeading &&
    !global.location.pathname
      .split('/')
      .some((param) => param === 'assignment' || param === 'assignment-builder');

  // initialize translation store, check if translation toggle should be shown
  useEffect(() => {
    if (!isReady) {
      const isAdaptive = global.location.pathname.split('/').includes('adaptive');
      init(getAssignmentIdFromPath(), isAdaptive);
    }
  }, [init, isReady]);

  useEffect(() => {
    if (!activeQuestion?.getId()) {
      return;
    }

    // the translation store handles early returning if translations are not meant to be shown
    // safe to call here
    fetchTranslation(activeQuestion, activeSubject);
  }, [activeQuestion, fetchTranslation, activeQuestionSet, activeSubject]);

  useEffect(() => {
    return () => reset();
  }, [reset]);

  // -------- Student views ------------

  if (displayOrientation === 'vertical' && !isMobile) {
    return (
      <QuestionComponentContentSplit
        actions={actions}
        activeQuestion={activeQuestion}
        activeQuestionSet={activeQuestionSet}
        activeQuestionStore={activeQuestionStore}
        activeSubject={activeSubject}
        ActiveQuestionInputArea={ActiveQuestionInputArea}
        answerInputPaneRef={answerInputPaneRef}
        customExplanation={customExplanation}
        customHOA={customHOA}
        customResultIndicator={customResultIndicator}
        guess={guess}
        hasQuestionSetAccessPredicate={hasQuestionSetAccessPredicate}
        interactive={interactive}
        isMobile={isMobile}
        navigateBackProps={navigateBackProps}
        navigateNextProps={navigateNextProps}
        onResize={onResize}
        postSubmissionScrollPointRef={postSubmissionScrollPointRef}
        questionState={questionState}
        resizerPosition={resizerPosition}
        partialCreditEnabled={partialCreditEnabled}
        shouldSetContentMaxWidth={shouldSetContentMaxWidth}
        useAdaptiveResizer={useAdaptiveResizer}
        shouldShowCopyright={shouldShowCopyright}
      />
    );
  }

  if (
    (displayOrientation === 'horizontal' && !isMobile) ||
    (isMobile && displayOrientation !== 'default')
  ) {
    return (
      <QuestionComponentHorizontalLayout
        actions={actions}
        activeQuestion={activeQuestion}
        activeQuestionSet={activeQuestionSet}
        activeQuestionStore={activeQuestionStore}
        activeSubject={activeSubject}
        ActiveQuestionInputArea={ActiveQuestionInputArea}
        customExplanation={customExplanation}
        customHOA={customHOA}
        customResultIndicator={customResultIndicator}
        guess={guess}
        hasQuestionSetAccessPredicate={hasQuestionSetAccessPredicate}
        interactive={interactive}
        isMobile={isMobile}
        navigateBackProps={navigateBackProps}
        navigateNextProps={navigateNextProps}
        postSubmissionScrollPointRef={postSubmissionScrollPointRef}
        questionState={questionState}
        stackedContainerRef={stackedContainerRef}
        partialCreditEnabled={partialCreditEnabled}
        shouldSetContentMaxWidth={shouldSetContentMaxWidth}
        shouldShowCopyright={shouldShowCopyright}
      />
    );
  }

  return (
    <form
      className='question-wrapper rounded-x1 border-border-primary background-primary border border-solid'
      onSubmit={(e) => {
        e.preventDefault();
      }}
    >
      <div className='question-wrapper__heading'>
        {shouldShowHeader && (
          <>
            <HeadingBar
              question={activeQuestion}
              questionSet={activeQuestionSet}
              navigateBackProps={navigateBackProps}
              navigateNextProps={navigateNextProps}
              headingActions={headingActions}
              modifyTitle={modifyTitle}
              showDifficulty={showDifficulty}
              customHeader={customHeader}
              leadingHeaderAction={leadingHeaderAction}
              truncateTitle={truncateTitle}
            />
          </>
        )}
      </div>
      <div className='question-wrapper__body'>
        <div className={classNames('p-x4 xs:p-x2', ANNOTATION_GUTTER_CLASS)}>
          {isArchivedQuestion && <ArchivedQuestionBanner classname='mb-x4' />}
          <Prompt question={activeQuestion} variant='full' />
          {showTranslations && <ReviewAnswersInEnglishBanner className='pb-x4' />}
          <QuestionContent
            activeQuestion={activeQuestion}
            activeQuestionSet={activeQuestionSet}
            activeQuestionStore={activeQuestionStore}
            activeSubject={activeSubject}
            ActiveQuestionInputArea={ActiveQuestionInputArea}
            customExplanation={customExplanation}
            customHOA={customHOA}
            customResultIndicator={customResultIndicator}
            guess={guess}
            interactive={interactive}
            isRedesign={false}
            postSubmissionScrollPointRef={postSubmissionScrollPointRef}
            questionState={questionState}
            partialCreditEnabled={partialCreditEnabled}
          />
        </div>
        {actions &&
          React.cloneElement(actions, {
            guess,
            hasAccess: hasQuestionSetAccessPredicate?.(activeQuestionSet) || false,
            navigateBackProps,
            navigateNextProps,
            question: activeQuestion,
            questionSet: activeQuestionSet,
            subject: activeSubject
          })}
      </div>
    </form>
  );
};
